import React, { useState, useEffect } from "react";
import { Axios, $, Modal } from "../../../Headers";

const statusOptions = [
  "Payment Verification Pending",
  "Payment Verified",
  "Payment Failed",
  "Payment Not Received",
  "Order Confirmed",
  "Processing / Packing",
  "Shipped / In Transit",
  "Order Delivered",
  "Order Cancelled",
];

const getStatusBadge = (status) => {
  switch (status) {
    case "Payment Verified":
      return <span className="badge badge-success px-2 py-1">Payment Verified</span>;
    case "Payment Verification Pending":
      return <span className="badge badge-warning text-dark px-2 py-1">Payment Verification Pending</span>;
    case "Payment Failed":
      return <span className="badge badge-danger px-2 py-1">Payment Failed</span>;
    case "Payment Not Received":
      return <span className="badge badge-danger px-2 py-1">Payment Not Received</span>;
    case "Order Confirmed":
      return <span className="badge badge-info px-2 py-1">Order Confirmed</span>;
    case "Processing / Packing":
      return <span className="badge badge-primary px-2 py-1">Processing / Packing</span>;
    case "Shipped / In Transit":
      return <span className="badge badge-dark px-2 py-1">Shipped / In Transit</span>;
    case "Order Delivered":
      return <span className="badge badge-success px-2 py-1">Order Delivered</span>;
    case "Order Cancelled":
      return <span className="badge badge-secondary px-2 py-1">Order Cancelled</span>;
    default:
      return <span className="badge badge-warning text-dark px-2 py-1">{status || "Payment Verification Pending"}</span>;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours).padStart(2, "0");
  return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
};

const getOrderBreakdown = (order) => {
  if (!order) return { saleSubtotal: 0, totalShipping: 0, grandTotal: 0 };
  let saleSubtotal = 0;
  let totalShipping = 0;
  if (order.items && order.items.length > 0) {
    order.items.forEach((it) => {
      const q = parseInt(it.quantity, 10) || 1;
      const s = parseInt(it.shipping_charges || it.shippingCharges || 0, 10) || 0;
      const p = parseInt(it.sale_price || it.salePrice || it.unit_price || it.unitPrice || 0, 10) || 0;
      saleSubtotal += (p * q);
      totalShipping += (s * q);
    });
  } else {
    saleSubtotal = parseInt(order.total_price, 10) || 0;
    totalShipping = 0;
  }
  const grandTotal = parseInt(order.total_price, 10) || (saleSubtotal + totalShipping);
  return { saleSubtotal, totalShipping, grandTotal };
};

const columnDefs = [
  { key: "index", label: "#", alwaysVisible: true },
  { key: "orderNumber", label: "Order ID", defaultVisible: true },
  { key: "bookTitle", label: "Book Title", defaultVisible: true },
  { key: "customerName", label: "Customer Name", defaultVisible: true },
  { key: "mobile", label: "Mobile", defaultVisible: true },
  { key: "quantity", label: "Qty", defaultVisible: true },
  { key: "itemsSubtotal", label: "Items Subtotal", defaultVisible: true },
  { key: "totalShipping", label: "Total Shipping", defaultVisible: true },
  { key: "totalPrice", label: "Total Price", defaultVisible: true },
  { key: "transactionId", label: "Txn ID", defaultVisible: true },
  { key: "trackingId", label: "Tracing ID", defaultVisible: true },
  { key: "status", label: "Status", defaultVisible: true },
  { key: "orderDate", label: "Order Date", defaultVisible: true },
  { key: "action", label: "Action", alwaysVisible: true },
];

const WebBookOrderDetails = () => {
  const [ordersData, setOrdersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusMsg, setStatusMsg] = useState("");

  // Column Visibility State
  const [columnVisibility, setColumnVisibility] = useState({
    orderNumber: true,
    bookTitle: true,
    customerName: true,
    mobile: true,
    quantity: true,
    itemsSubtotal: true,
    totalShipping: true,
    totalPrice: true,
    transactionId: true,
    trackingId: true,
    status: true,
    orderDate: true,
  });
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const toggleColumn = (key) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const selectAllColumns = (val) => {
    setColumnVisibility({
      orderNumber: val,
      bookTitle: val,
      customerName: val,
      mobile: val,
      quantity: val,
      itemsSubtotal: val,
      totalShipping: val,
      totalPrice: val,
      transactionId: val,
      trackingId: val,
      status: val,
      orderDate: val,
    });
  };

  // Status Change Log Audit State
  const [statusLogs, setStatusLogs] = useState([]);
  const [loadingStatusLogs, setLoadingStatusLogs] = useState(false);

  // Search & Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination State (10 items per page)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;

  // Debounce search input (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchOrders = () => {
    setLoading(true);
    const serverName = window.$name;

    const fd = new FormData();
    fd.append("search", debouncedSearchTerm);
    fd.append("status", statusFilter);
    fd.append("startDate", startDate);
    fd.append("endDate", endDate);
    fd.append("page", currentPage);
    fd.append("limit", itemsPerPage);

    Axios.post("//" + serverName + "/controller/GetWebBookOrdersController.php", fd)
      .then((res) => {
        if (res.data) {
          if (Array.isArray(res.data)) {
            setOrdersData(res.data);
            setTotalRecords(res.data.length);
            setTotalPages(Math.ceil(res.data.length / itemsPerPage) || 1);
          } else if (res.data.orders) {
            setOrdersData(res.data.orders);
            setTotalRecords(res.data.totalRecords || 0);
            setTotalPages(res.data.totalPages || 1);
          }
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching book orders:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchOrders();
  }, [debouncedSearchTerm, statusFilter, startDate, endDate, currentPage]);

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;

  const fetchStatusLogs = (orderId) => {
    if (!orderId) return;
    setLoadingStatusLogs(true);
    const serverName = window.$name;
    const fd = new FormData();
    fd.append("orderId", orderId);

    Axios.post("//" + serverName + "/controller/GetWebBookOrderStatusLogsController.php", fd)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          setStatusLogs(res.data);
        } else {
          setStatusLogs([]);
        }
        setLoadingStatusLogs(false);
      })
      .catch((err) => {
        console.error("Error fetching status logs:", err);
        setLoadingStatusLogs(false);
      });
  };

  const [trackingWebhookLogs, setTrackingWebhookLogs] = useState([]);
  const [loadingTrackingLogs, setLoadingTrackingLogs] = useState(false);

  const fetchTrackingLogs = (orderId) => {
    if (!orderId) return;
    setLoadingTrackingLogs(true);
    const serverName = window.$name;
    const fd = new FormData();
    fd.append("orderId", orderId);

    Axios.post("//" + serverName + "/controller/GetWebBookOrderTrackingLogsController.php", fd)
      .then((res) => {
        if (res.data && Array.isArray(res.data)) {
          setTrackingWebhookLogs(res.data);
        } else {
          setTrackingWebhookLogs([]);
        }
        setLoadingTrackingLogs(false);
      })
      .catch((err) => {
        console.error("Error fetching tracking webhook logs:", err);
        setLoadingTrackingLogs(false);
      });
  };

  const [trackingId, setTrackingId] = useState("");
  const [trackingLink, setTrackingLink] = useState("");

  const openOrderModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.order_status || "Payment Verification Pending");
    setTrackingId(order.tracking_id || "");
    setTrackingLink(order.tracking_link || "");
    setStatusMsg("");
    setModalIsOpen(true);
    fetchStatusLogs(order.id);
    fetchTrackingLogs(order.id);
  };

  const handleUpdateStatus = () => {
    if (!selectedOrder || !newStatus) return;

    setUpdatingStatus(true);
    setStatusMsg("");
    const serverName = window.$name;

    const fd = new FormData();
    fd.append("orderId", selectedOrder.id);
    fd.append("orderStatus", newStatus);
    fd.append("trackingId", trackingId);
    fd.append("trackingLink", trackingLink);

    Axios.post("//" + serverName + "/controller/UpdateWebBookOrderStatusController.php", fd)
      .then((res) => {
        setUpdatingStatus(false);
        if (res.data && res.data.Success === 1) {
          setStatusMsg("Status, Tracing ID, and Tracking Link updated successfully!");
          setSelectedOrder({ ...selectedOrder, order_status: newStatus, tracking_id: trackingId, tracking_link: trackingLink });
          fetchOrders();
          fetchStatusLogs(selectedOrder.id);
          fetchTrackingLogs(selectedOrder.id);
        } else {
          setStatusMsg(res.data?.value?.Msg || "Failed to update status.");
        }
      })
      .catch((err) => {
        console.error("Error updating status:", err);
        setUpdatingStatus(false);
        setStatusMsg("Error updating status.");
      });
  };

  const visibleColCount = 2 + Object.values(columnVisibility).filter(Boolean).length;

  return (
    <div>
      <div id="ss-uad-article-01">
        <div className="row" id="ss-uad-article-02">
          <div className="col-6" style={{ display: "flex", alignItems: "center" }}>
            <p id="ss-uad-admin-02">Book Order Details</p>
          </div>
        </div>

        <div className="card-body" id="card_body">
          <div className="container-fluid">
            {/* Filter & Search Bar */}
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-body bg-light rounded p-3">
                <div className="row align-items-end">
                  {/* Text Search */}
                  <div className="col-md-3 mb-2">
                    <label className="font-weight-bold text-dark small mb-1">Search Orders</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Order ID, Name, Txn ID, City..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  {/* Status Dropdown Filter */}
                  <div className="col-md-3 mb-2">
                    <label className="font-weight-bold text-dark small mb-1">Filter Status</label>
                    <select
                      className="form-control form-control-sm"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="ALL">All Statuses ({ordersData.length})</option>
                      {statusOptions.map((st, i) => (
                        <option key={i} value={st}>
                          {st}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Start Date */}
                  <div className="col-md-2 mb-2">
                    <label className="font-weight-bold text-dark small mb-1">From Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  {/* End Date */}
                  <div className="col-md-2 mb-2">
                    <label className="font-weight-bold text-dark small mb-1">To Date</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>

                  {/* Reset Filters */}
                  <div className="col-md-2 mb-2">
                    <button
                      type="button"
                      className="btn btn-sm btn-secondary btn-block"
                      onClick={() => {
                        setSearchTerm("");
                        setStatusFilter("ALL");
                        setStartDate("");
                        setEndDate("");
                      }}
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Column Customizer Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="text-muted small">
                Total Orders Found: <strong className="text-dark">{totalRecords}</strong>
              </div>

              <div className="position-relative">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary font-weight-bold shadow-sm"
                  onClick={() => setShowColumnPicker(!showColumnPicker)}
                >
                  <i className="fa fa-columns mr-1"></i> Customize Columns ({visibleColCount - 2}/{columnDefs.length - 2}) <i className={`fa fa-chevron-${showColumnPicker ? "up" : "down"} ml-1`}></i>
                </button>

                {showColumnPicker && (
                  <div
                    className="card position-absolute shadow-lg p-3 bg-white rounded"
                    style={{
                      right: 0,
                      top: "100%",
                      zIndex: 1050,
                      minWidth: "250px",
                      marginTop: "6px",
                      border: "1px solid #007bff",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                      <strong className="text-primary small">
                        <i className="fa fa-sliders-h mr-1"></i> Show / Hide Columns
                      </strong>
                      <button
                        type="button"
                        className="close p-0 m-0"
                        style={{ fontSize: "1.2rem", lineHeight: "1" }}
                        onClick={() => setShowColumnPicker(false)}
                      >
                        &times;
                      </button>
                    </div>

                    <div className="d-flex justify-content-between mb-2">
                      <button
                        type="button"
                        className="btn btn-xs btn-link p-0 small text-primary"
                        onClick={() => selectAllColumns(true)}
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-link p-0 small text-danger"
                        onClick={() => selectAllColumns(false)}
                      >
                        Deselect All
                      </button>
                    </div>

                    <div style={{ maxHeight: "240px", overflowY: "auto" }}>
                      {columnDefs.map((col) => {
                        if (col.alwaysVisible) return null;
                        return (
                          <div key={col.key} className="form-check custom-control custom-checkbox mb-1">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id={`col-toggle-${col.key}`}
                              checked={!!columnVisibility[col.key]}
                              onChange={() => toggleColumn(col.key)}
                            />
                            <label
                              className="custom-control-label small text-dark"
                              htmlFor={`col-toggle-${col.key}`}
                              style={{ cursor: "pointer", userSelect: "none" }}
                            >
                              {col.label}
                            </label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading orders...</span>
                </div>
              </div>
            ) : (
              <div>
                <div className="table-responsive">
                  <table
                    className="table table-striped table-hover table-bordered shadow-sm bg-white"
                    style={{ width: "100%", fontSize: "0.9rem" }}
                  >
                    <thead className="thead-light">
                      <tr className="text-center">
                        <th>#</th>
                        {columnVisibility.orderNumber && <th>Order ID</th>}
                        {columnVisibility.bookTitle && <th>Book Title</th>}
                        {columnVisibility.customerName && <th>Customer Name</th>}
                        {columnVisibility.mobile && <th>Mobile</th>}
                        {columnVisibility.quantity && <th>Qty</th>}
                        {columnVisibility.itemsSubtotal && <th>Items Subtotal</th>}
                        {columnVisibility.totalShipping && <th>Total Shipping</th>}
                        {columnVisibility.totalPrice && <th>Total Price</th>}
                        {columnVisibility.transactionId && <th>Txn ID</th>}
                        {columnVisibility.trackingId && <th>Tracing ID</th>}
                        {columnVisibility.status && <th>Status</th>}
                        {columnVisibility.orderDate && <th>Order Date</th>}
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordersData.length > 0 ? (
                        ordersData.map((data, idx) => {
                          const bd = getOrderBreakdown(data);
                          return (
                            <tr key={data.id || idx} className="text-center">
                              <td>{indexOfFirstItem + idx + 1}</td>
                              {columnVisibility.orderNumber && (
                                <td className="font-weight-bold text-dark">{data.order_number || "ORD" + data.id}</td>
                              )}
                              {columnVisibility.bookTitle && (
                                <td className="font-weight-bold">
                                  {data.items && data.items.length > 1 ? (
                                    <div>
                                      <span>{data.items[0]?.book_title || data.book_title || "Book Item"}</span>
                                      <span
                                        className="badge badge-primary ml-1 px-2 py-1"
                                        title={`Total ${data.items.length} titles in this order`}
                                      >
                                        +{data.items.length - 1} more
                                      </span>
                                    </div>
                                  ) : (
                                    <span>{data.book_title || (data.items && data.items[0]?.book_title) || "N/A"}</span>
                                  )}
                                </td>
                              )}
                              {columnVisibility.customerName && (
                                <td>{`${data.first_name || ""} ${data.last_name || ""}`}</td>
                              )}
                              {columnVisibility.mobile && <td>{data.mobile}</td>}
                              {columnVisibility.quantity && <td>{data.copy}</td>}
                              {columnVisibility.itemsSubtotal && (
                                <td className="text-dark font-weight-bold">₹{bd.saleSubtotal}</td>
                              )}
                              {columnVisibility.totalShipping && (
                                <td className="text-info font-weight-bold">
                                  {bd.totalShipping > 0 ? `₹${bd.totalShipping}` : "Free"}
                                </td>
                              )}
                              {columnVisibility.totalPrice && (
                                <td className="text-success font-weight-bold">
                                  {data.total_price ? `₹${data.total_price}` : "-"}
                                </td>
                              )}
                              {columnVisibility.transactionId && (
                                <td className="font-weight-bold text-primary">{data.transaction_id || "-"}</td>
                              )}
                              {columnVisibility.trackingId && (
                                <td className="font-weight-bold text-success font-monospace" style={{ fontFamily: "monospace" }}>
                                  {data.tracking_id || "-"}
                                </td>
                              )}
                              {columnVisibility.status && <td>{getStatusBadge(data.order_status)}</td>}
                              {columnVisibility.orderDate && (
                                <td className="font-weight-bold">{formatDate(data.created_at)}</td>
                              )}
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-info"
                                  onClick={() => openOrderModal(data)}
                                >
                                  <i className="fa fa-eye mr-1"></i> View / Edit Status
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={visibleColCount} className="text-center text-muted py-4">
                            No book orders found matching your search and filter criteria.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                  <div className="text-muted small">
                    Showing {totalRecords > 0 ? indexOfFirstItem + 1 : 0} to{" "}
                    {Math.min(indexOfFirstItem + itemsPerPage, totalRecords)} of {totalRecords} total orders
                  </div>

                  <nav>
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        >
                          Previous
                        </button>
                      </li>

                      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                        <li
                          key={page}
                          className={`page-item ${currentPage === page ? "active" : ""}`}
                        >
                          <button
                            type="button"
                            className="page-link"
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        </li>
                      ))}

                      <li
                        className={`page-item ${
                          currentPage === totalPages || totalPages === 0 ? "disabled" : ""
                        }`}
                      >
                        <button
                          type="button"
                          className="page-link"
                          onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Order Details & Status Modal */}
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        contentLabel="Full Order Details"
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            maxWidth: "680px",
            width: "92%",
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: "10px",
            padding: "25px",
          },
          overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1050,
          },
        }}
      >
        {selectedOrder && (() => {
          const modalBd = getOrderBreakdown(selectedOrder);
          return (
            <div>
              <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
                <h4 className="text-primary mb-0">
                  Order Information ({selectedOrder.order_number || "ORD" + selectedOrder.id})
                </h4>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setModalIsOpen(false)}
                >
                  &times;
                </button>
              </div>

              {/* Status Update Control Box */}
              <div className="bg-light p-3 rounded mb-3 border">
                <h6 className="font-weight-bold text-dark mb-2">
                  <i className="fa fa-edit text-primary mr-1"></i> Update Order Status & Tracing ID:
                </h6>
                <div className="form-group mb-2">
                  <label className="font-weight-bold text-dark small mb-1">Select Order Status:</label>
                  <select
                    className="form-control form-control-sm"
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                  >
                    {statusOptions.map((status, index) => (
                      <option key={index} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                {(newStatus.toLowerCase().includes("shipped") || newStatus.toLowerCase().includes("transit") || (trackingId && trackingId.trim() !== "") || (trackingLink && trackingLink.trim() !== "")) && (
                  <div className="form-group mb-2 bg-white p-2 rounded border">
                    <div className="mb-2">
                      <label className="font-weight-bold text-dark small mb-1">
                        Courier Tracing ID / AWB Tracking Number: <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Enter courier tracing ID / AWB tracking number (e.g. AWB123456789)"
                        value={trackingId}
                        onChange={(e) => setTrackingId(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="font-weight-bold text-dark small mb-1">
                        Courier Tracking Link / URL (Optional):
                      </label>
                      <input
                        type="url"
                        className="form-control form-control-sm"
                        placeholder="https://courier.com/track/AWB123456789 (Optional)"
                        value={trackingLink}
                        onChange={(e) => setTrackingLink(e.target.value)}
                      />
                    </div>
                    <small className="text-muted d-block mt-1">Tracing ID & link will be saved to DB and emailed to customer.</small>
                  </div>
                )}
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-sm btn-primary font-weight-bold px-3"
                    type="button"
                    disabled={updatingStatus}
                    onClick={handleUpdateStatus}
                  >
                    {updatingStatus ? (
                      <span><i className="fa fa-spinner fa-spin mr-1"></i> Updating & Sending Email...</span>
                    ) : (
                      <span><i className="fa fa-paper-plane mr-1"></i> Update Status & Email User</span>
                    )}
                  </button>
                </div>
                {statusMsg && (
                  <div className="small font-weight-bold mt-2 text-success">
                    {statusMsg}
                  </div>
                )}
              </div>

              <div className="row">
                <div className="col-md-6 mb-2">
                  <strong>Order ID:</strong>
                  <p className="font-weight-bold text-dark mb-1">{selectedOrder.order_number || "ORD" + selectedOrder.id}</p>
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Current Status:</strong>
                  <p className="mb-1">{getStatusBadge(selectedOrder.order_status)}</p>
                </div>

                {/* Ordered Book Items Table */}
                <div className="col-12 mb-2">
                  <strong className="d-block mb-1 text-primary">Ordered Books ({selectedOrder.items ? selectedOrder.items.length : 1}):</strong>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm bg-white mb-1">
                      <thead className="thead-light">
                        <tr>
                          <th>#</th>
                          <th>Book Title</th>
                          <th>ISBN</th>
                          <th>Sale Price</th>
                          <th>Shipping</th>
                          <th>Qty</th>
                          <th>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedOrder.items || []).map((it, iIdx) => {
                          const p = it.sale_price || it.salePrice || it.unit_price || 0;
                          const s = it.shipping_charges || it.shippingCharges || 0;
                          return (
                            <tr key={iIdx}>
                              <td>{iIdx + 1}</td>
                              <td className="font-weight-bold">{it.book_title || selectedOrder.book_title || "N/A"}</td>
                              <td>{it.isbn || selectedOrder.isbn || "N/A"}</td>
                              <td>₹{p}</td>
                              <td className="text-info">{parseInt(s, 10) > 0 ? `+ ₹${s}` : "Free"}</td>
                              <td className="text-center font-weight-bold">{it.quantity || selectedOrder.copy || 1}</td>
                              <td className="text-success font-weight-bold">₹{it.subtotal || selectedOrder.total_price}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Separate Total Shipping & Subtotal Summary Box */}
                  <div className="bg-light p-2 px-3 rounded border mb-2 mt-2">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted small">Items Sale Subtotal:</span>
                      <span className="font-weight-bold text-dark">₹{modalBd.saleSubtotal}</span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="text-muted small">Total Shipping Charges:</span>
                      <span className="font-weight-bold text-info">
                        {modalBd.totalShipping > 0 ? `+ ₹${modalBd.totalShipping}` : "FREE"}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between align-items-center pt-1 border-top">
                      <strong className="text-dark">Grand Total Order Amount:</strong>
                      <span className="h5 text-success font-weight-bold mb-0">₹{selectedOrder.total_price}</span>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 mb-2">
                  <strong>Customer Name:</strong>
                  <p className="text-secondary mb-1">{`${selectedOrder.first_name} ${selectedOrder.last_name}`}</p>
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Email:</strong>
                  <p className="text-secondary mb-1">{selectedOrder.email}</p>
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Mobile:</strong>
                  <p className="text-secondary mb-1">{selectedOrder.mobile}</p>
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Total Quantity:</strong>
                  <p className="text-secondary mb-1 font-weight-bold">{selectedOrder.copy} books</p>
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Transaction ID:</strong>
                  <p className="text-primary font-weight-bold mb-1">{selectedOrder.transaction_id || "N/A"}</p>
                </div>
                <div className="col-md-6 mb-2">
                  <strong>Tracing / Tracking ID:</strong>
                  <p className="text-success font-weight-bold mb-1" style={{ fontFamily: "monospace" }}>
                    {selectedOrder.tracking_id ? `📦 ${selectedOrder.tracking_id}` : "N/A"}
                  </p>
                </div>
                {selectedOrder.tracking_link && (
                  <div className="col-12 mb-2">
                    <strong>Tracking Link / URL:</strong>
                    <p className="mb-1">
                      <a href={selectedOrder.tracking_link} target="_blank" rel="noopener noreferrer" className="btn btn-xs btn-outline-primary font-weight-bold">
                        <i className="fa fa-external-link mr-1"></i> {selectedOrder.tracking_link}
                      </a>
                    </p>
                  </div>
                )}
                <div className="col-md-6 mb-2">
                  <strong>Order Date & Time:</strong>
                  <p className="text-secondary mb-1 font-weight-bold">{formatDateTime(selectedOrder.created_at)}</p>
                </div>
                <div className="col-12 mb-2">
                  <strong>City / State / Pincode:</strong>
                  <p className="text-secondary mb-1">{`${selectedOrder.city || ""}, ${selectedOrder.state || ""} - ${selectedOrder.pincode || ""}`}</p>
                </div>
                <div className="col-12 mb-2">
                  <strong>Delivery Address:</strong>
                  <p className="text-secondary mb-1 bg-light p-2 rounded">{selectedOrder.address}</p>
                </div>
                {selectedOrder.payment_note && (
                  <div className="col-12 mb-2">
                    <strong>Payment / Transaction Note:</strong>
                    <p className="text-secondary mb-1 bg-light p-2 rounded">{selectedOrder.payment_note}</p>
                  </div>
                )}

                {/* Status Update History Audit Log */}
                <div className="col-12 mt-3 pt-3 border-top">
                  <strong className="d-block mb-2 text-dark">
                    <i className="fa fa-history mr-1 text-primary"></i>Status Change Audit Log History:
                  </strong>
                  {loadingStatusLogs ? (
                    <div className="text-center py-2 text-muted small">
                      <div className="spinner-border spinner-border-sm mr-2 text-primary" role="status"></div>
                      Loading status audit logs...
                    </div>
                  ) : statusLogs.length > 0 ? (
                    <div className="table-responsive" style={{ maxHeight: "180px", overflowY: "auto" }}>
                      <table className="table table-bordered table-sm bg-white mb-0" style={{ fontSize: "0.85rem" }}>
                        <thead className="thead-light">
                          <tr>
                            <th>#</th>
                            <th>Date & Time</th>
                            <th>Previous Status</th>
                            <th>Updated Status</th>
                            <th>Updated By</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statusLogs.map((log, lIdx) => (
                            <tr key={log.id || lIdx}>
                              <td>{statusLogs.length - lIdx}</td>
                              <td className="text-muted font-weight-bold">{formatDateTime(log.created_at)}</td>
                              <td>{log.old_status ? getStatusBadge(log.old_status) : <span className="text-muted">-</span>}</td>
                              <td>{getStatusBadge(log.new_status)}</td>
                              <td><span className="badge badge-secondary px-2">{log.updated_by || "Admin"}</span></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted small italic p-2 bg-light rounded">
                      No status audit log entries recorded yet.
                    </div>
                  )}
                </div>

                {/* TrackingMore Webhook Tracking Timeline Log */}
                <div className="col-12 mt-3 pt-3 border-top">
                  <strong className="d-block mb-2 text-dark">
                    <i className="fa fa-truck mr-1 text-info"></i>TrackingMore Webhook Real-time Updates Timeline:
                  </strong>
                  {loadingTrackingLogs ? (
                    <div className="text-center py-2 text-muted small">
                      <div className="spinner-border spinner-border-sm mr-2 text-info" role="status"></div>
                      Loading tracking timeline updates...
                    </div>
                  ) : trackingWebhookLogs.length > 0 ? (
                    <div className="table-responsive" style={{ maxHeight: "200px", overflowY: "auto" }}>
                      <table className="table table-bordered table-sm bg-white mb-0" style={{ fontSize: "0.85rem" }}>
                        <thead className="thead-light">
                          <tr>
                            <th>#</th>
                            <th>Date & Time</th>
                            <th>Tracking ID</th>
                            <th>Courier</th>
                            <th>Status</th>
                            <th>Latest Event</th>
                            <th>Location</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trackingWebhookLogs.map((log, tIdx) => (
                            <tr key={log.id || tIdx}>
                              <td>{trackingWebhookLogs.length - tIdx}</td>
                              <td className="text-muted font-weight-bold">{formatDateTime(log.created_at)}</td>
                              <td className="font-monospace text-primary" style={{ fontFamily: "monospace" }}>{log.tracking_id}</td>
                              <td><span className="badge badge-dark text-uppercase">{log.courier_code || "Courier"}</span></td>
                              <td>
                                <span className={`badge ${log.delivery_status === 'delivered' ? 'badge-success' : 'badge-info'} text-capitalize`}>
                                  {log.delivery_status || "Update"}
                                </span>
                              </td>
                              <td className="font-weight-bold text-dark">{log.latest_event || "-"}</td>
                              <td>{log.location || "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted small italic p-2 bg-light rounded">
                      No webhook tracking updates received for this order yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="text-right mt-3 pt-2 border-top">
                <button
                  className="btn btn-secondary"
                  onClick={() => setModalIsOpen(false)}
                >
                  Close
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
};

export default WebBookOrderDetails;
